import apollo from "@/apollo.js"
import gql from "graphql-tag"
import Vue from "vue"
import Helpers from "../helpers"

export const updateCase = async (caseData) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation updateCase($updatedCase: UpdateCaseInput!) {
          updateCase(updatedCase: $updatedCase) {
            message 
            error
            lowLevelErrorMessage 
            invalidFields
            case {
              id
              version
              summaryVal1
              summaryVal2
              summaryVal3
              summaryVal4
              summaryVal5
          
              summaryVal1Defn
              summaryVal2Defn
              summaryVal3Defn
              summaryVal4Defn
              summaryVal5Defn
                            
              caseTypeId
              caseType
              title
              currentState
              properties
              createdAt
              updatedAt 
              lastExportedAt
              lastExportedViaBucketId
              noUnreadConversations
              unresolvedTaskFailures
              tasklists {
                id
                tasks {
                  id
                  name
                  task { actionType fieldset }
                }
              }
              currentlyViewing {
                userId
                userDisplayName
              }
              conversationCount
              fileAttachments {
                id
                userDisplayName
                url
                createdAt
                updatedAt
                size
                filename
                extension
                contentType
              }
              notes {
                id
                subject
                body 
                author {
                  id
                  displayName
                }
                createdAt
                updatedAt
              }
              schema {
                name
                primary_key
                events { preCreate postCreate preStateChange postStateChange preSave postSave preDelete postDelete }
                case_data_views { 
                  new { rows { columns { fieldset } }} 
                  edit { rows { columns { fieldset } }}
                  detail { rows { columns { fieldset } }}
                }
                fieldsets { 
                  name  
                  display_name
                  display_toggle_field
                  fields {
                    name
                    type
                    size
                    display_name
                    guidance
                    default_value
                    format
                    readonly
                    hidden
                    required
                    display
                    symbol
                    precision
                    cardinality
                    item_type
                    dependent_on
                    formula
                    validation
                    validation_message
                    options
                    cascading_parent_field
                    uif
                    time_format
                    input_format
                    input_timezone
                    relation
                    lookup_defaults { lookup_field_name type value }                    
                    cascading_options { parent_field_option options }
                    fields {
                      name
                      type
                      size
                      display_name
                      guidance
                      default_value
                      format
                      readonly
                      hidden
                      options
                      required
                      display
                      symbol
                      cardinality
                      item_type
                      precision
                      dependent_on
                      formula
                      uif
                      time_format 
                      input_format
                      input_timezone
                      relation
                      lookup_defaults { lookup_field_name type value }   
                      cascading_parent_field
                      cascading_options { parent_field_option options } 
                    }
                    display_toggle_field
                  }
                }
              }
              properties
            }
          }
        }
      `),
    variables: { updatedCase: caseData },
  })
  return response.data.updateCase
}

export const createCase = async (data) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation createCase($newCase: NewCaseInput!) {
          createCase(newCase: $newCase) {
            message error caseData lowLevelErrorMessage
          }
        }`),
    variables: { newCase: data },
  })
  return response.data
}

export const deleteCase = async (caseId) => {
  const response = await apollo.mutate({
    mutation: gql(`
      mutation deleteCase($id: Int!) {
        deleteCase(id: $id) { message error lowLevelErrorMessage }
      }`),
    variables: { id: caseId },
  })
  return response.data.deleteCase
}

export const deleteCases = async (ids) => {
  const response = await apollo.mutate({
    mutation: gql(`
      mutation deleteCases($ids: [Int!]!) {
        deleteCases(ids: $ids) { message error lowLevelErrorMessage deletedCount failedCount }
      }`),
    variables: { ids: ids },
  })
  return response.data.deleteCases
}

export const deleteBucketCases = async (bucketId, confirmedCount, searchField, searchTerm) => {
  const response = await apollo.mutate({
    mutation: gql(`
      mutation deleteBucketCases($bucketId: Int!, $confirmedCount: Int!, $searchField: String, $searchTerm: String) {
        deleteBucketCases(bucketId: $bucketId, confirmedCount: $confirmedCount, searchField: $searchField, searchTerm: $searchTerm) {
          message error lowLevelErrorMessage deletedCount failedCount
        }
      }`),
    variables: { bucketId: bucketId, confirmedCount: confirmedCount, searchField: searchField, searchTerm: searchTerm },
  })
  return response.data.deleteBucketCases
}

export const deleteSearchCases = async (caseTypeId, searchTerm, confirmedCount) => {
  const response = await apollo.mutate({
    mutation: gql(`
      mutation deleteSearchCases($caseTypeId: Int!, $searchTerm: String!, $confirmedCount: Int!) {
        deleteSearchCases(caseTypeId: $caseTypeId, searchTerm: $searchTerm, confirmedCount: $confirmedCount) {
          message error lowLevelErrorMessage deletedCount failedCount
        }
      }`),
    variables: { caseTypeId: caseTypeId, searchTerm: searchTerm, confirmedCount: confirmedCount },
  })
  return response.data.deleteSearchCases
}

export const addCaseMember = async (caseId, participantId, participantType) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation addCaseParticipant($caseId: String!, $participantId: Int!, $participantType: UserOrTeam!) {
              addCaseParticipant(caseId: $caseId, participantId: $participantId, participantType: $participantType) {
                error
                lowLevelErrorMessage
                message
              }
            }
        `),
    variables: { caseId: caseId, participantId: participantId, participantType: participantType },
  })
  return response.data
}

export const removeCaseMember = async (caseId, participantId, participantType) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation removeCaseParticipant($caseId: String!, $participantId: Int!, $participantType: UserOrTeam!) {
              removeCaseParticipant(caseId: $caseId, participantId: $participantId, participantType: $participantType) {
                error
                lowLevelErrorMessage
                message
              }
            }
        `),
    variables: { caseId: caseId, participantId: participantId, participantType: participantType },
  })
  return response.data
}

export const findRelatedCases = async (caseTypeName, relatedData, page, pageSize, sortField, sortDirection) => {
  const response = await apollo.query({
    query: gql(`
            query findRelatedCases($caseTypeName: String!, $relatedKey: String!, $relatedId: Int!, $fromId: String!, $page: Int!, $pageSize: Int!, $sortField: String, $sortDirection: String) {
              findRelatedCases(caseTypeName: $caseTypeName, relatedKey: $relatedKey, relatedId: $relatedId, fromId: $fromId, page: $page, pageSize: $pageSize, sortField: $sortField, sortDirection: $sortDirection) {
                error
                message
                total
                relatedCases { id title currentState caseTypeId caseType properties createdAt updatedAt lastExportedAt lastExportedViaBucketId noUnreadConversations unresolvedTaskFailures }
              }
            }
        `),
    variables: { caseTypeName: caseTypeName, relatedKey: relatedData.fromRelationKey, relatedId: relatedData.id, fromId: relatedData.relatedCaseId, page, pageSize, sortField, sortDirection },
  })
  return response.data.findRelatedCases
}

export const findCaseSummary = async (caseId) => {
  const response = await apollo.query({
    query: gql(`
    query case($caseId: String! ) {
      case(caseId: $caseId, ignoreTimeline: true) {
        summaryVal1
        summaryVal2
        summaryVal3
        summaryVal4
        summaryVal5
    
        summaryVal1Defn
        summaryVal2Defn
        summaryVal3Defn
        summaryVal4Defn
        summaryVal5Defn

        uniqueIdentifierField
        uniqueIdentifierValue
      }
    }
    `),
    variables: { caseId: "" + caseId },
  })
  if (response && response.data && response.data.case) {
    const kase = response.data.case
    return kase
  }
  return undefined
}

export const findCase = async (caseId) => {
  const response = await apollo.query({
    query: gql(`
    query case($caseId: String! ) {
      case(caseId: $caseId) {
        id
        version
        summaryVal1
        summaryVal2
        summaryVal3
        summaryVal4
        summaryVal5
    
        summaryVal1Defn
        summaryVal2Defn
        summaryVal3Defn
        summaryVal4Defn
        summaryVal5Defn        
        caseTypeId
        caseType
        title
        currentState
        properties
        createdAt
        updatedAt
        lastExportedAt
        lastExportedViaBucketId
        noUnreadConversations
        unresolvedTaskFailures
        serviceLevels {
          active
          runningTime
          jeopardizedAt
          breachedAt
          serviceLevelCriteria { id name }
        }
        tasklists {
          id
          name
          position
          templated
          tasks {
            id
            name
            description
            position
            currentStatus
            unresolvedFailures
            assignedToTeam { id }
            assignedToUser { id }
            scheduledType
            scheduledTime
            templated
            task { actionType fieldset }
          }
        }        
        currentlyViewing {
          userId
          userDisplayName
        }
        conversationCount
        fileAttachments {
          id
          userDisplayName
          url
          createdAt
          updatedAt
          size
          filename
          extension
          contentType
          hidden
        }
        notes {
          id
          subject
          body 
          author {
            id
            displayName
          }
          createdAt
          updatedAt
        }
        schema {
          name
          primary_key
          events { preCreate postCreate preStateChange postStateChange preSave postSave preDelete postDelete }
          case_data_views { 
            new { rows { columns { fieldset } }} 
            edit { rows { columns { fieldset } }}
            detail { rows { columns { fieldset } }}
          }
          fieldsets { 
            name            
            display_name
            display_toggle_field
            fields {
              name
              type
              size
              display_name
              guidance
              precision
              default_value
              format
              readonly
              hidden
              required
              display
              symbol
              cardinality
              item_type
              dependent_on
              formula
              validation
              validation_message
              options
              cascading_parent_field
              time_format
              input_format
              input_timezone
              uif
              relation
              cascading_options { parent_field_option options }
              lookup_defaults { lookup_field_name type value }              
              fields {
                name
                type
                size
                display_name
                guidance
                precision
                default_value
                format
                readonly
                hidden
                options
                required
                display
                symbol
                cardinality
                item_type
                dependent_on
                formula
                time_format
                input_format
                input_timezone
                uif
                relation
                lookup_defaults { lookup_field_name type value }
                cascading_parent_field
                cascading_options { parent_field_option options }         
              }
              display_toggle_field
            }
          }
        }
        properties
      }
    }
    `),
    variables: { caseId: "" + caseId },
  })
  return response.data
}

export const getCaseConversationsPage = async (caseId, page = 1, pageSize = 100) => {
  const response = await apollo.query({
    query: gql(`
      query caseConversations($caseId: String!, $page: Int!, $pageSize: Int!) {
        caseConversations(caseId: $caseId, page: $page, pageSize: $pageSize) {
          total
          page
          pageSize
          hasMore
          conversations {
            id
            subject
            createdAt
            updatedAt
            sentimentScore
            sentimentMagnitude
            unread
            messages {
              id
              subject
              attachments {
                id
                url
                createdAt
                updatedAt
                size
                filename
                extension
                contentType
              }
              createdAt
              updatedAt
              archivedAt
              markedReadAt
              sentimentScore
              sentimentMagnitude
              sendStatus
              sendStatusDetail
              sendVerifiedAt
              from { id displayName type email userId teamId }
              markedReadBy
              archivedBy
              recipients { id displayName type email userId teamId }
              toRecipients { id displayName type email userId teamId }
              ccRecipients { id displayName type email userId teamId }
              bccRecipients { id displayName type email userId teamId }
            }
          }
        }
      }
    `),
    variables: { caseId: "" + caseId, page, pageSize },
  })
  return response.data.caseConversations
}

export const findCaseAttachments = async (caseId) => {
  const response = await apollo.query({
    query: gql(`
    query case($caseId: String! ) {
      case(caseId: $caseId) {
        fileAttachments {
          id
          userDisplayName
          url
          createdAt
          updatedAt
          size
          filename
          extension
          contentType
          hidden
        }
      }
    }
    `),
    variables: { caseId: "" + caseId },
  })
  return response.data.case.fileAttachments
}

export const getCaseSearch = async (caseType, page, pageSize, query, fieldsToReturn) => {
  page = page + 1
  try {
    const response = await apollo.query({
      query: gql(`
          query search($caseType: String!, $page: Int!, $pageSize: Int!, $query: String!, $fields: [String]!) {
            search(caseType: $caseType, page: $page, pageSize: $pageSize, query: $query, fields: $fields) {
              _id title properties
            }
          }
        `),
      variables: { caseType, page, pageSize, query, fields: fieldsToReturn },
    })
    return response.data.search
  } catch (error) {
    console.error(error)
  }
}

const searchFailure = (error) => {
  const messages = []
  const graphQLErrors = error && error.graphQLErrors
  if (graphQLErrors && graphQLErrors.length) {
    graphQLErrors.forEach((entry) => {
      if (entry && entry.message) {
        messages.push(entry.message)
      }
    })
  }
  const networkErrors = error && error.networkError && error.networkError.result && error.networkError.result.errors
  if (networkErrors && networkErrors.length) {
    networkErrors.forEach((entry) => {
      if (entry && entry.message && messages.indexOf(entry.message) === -1) {
        messages.push(entry.message)
      }
    })
  }
  if (messages.length) {
    return messages.join("; ")
  }
  if (error && error.message) {
    return error.message
  }
  return "Search did not return a valid response."
}

const invalidSearchResponse = (error) => ({
  error: true,
  message: searchFailure(error),
  results: [],
  documents: { documents: [], total: 0, error: true }
})

export const getGlobalSearch = async (searchTerm, page, pageSize) => {
  try {
    const response = await apollo.query({
      query: gql(`
          query globalSearch($searchTerm: String!, $page: Int, $pageSize: Int) {
            globalSearch(searchTerm: $searchTerm, page: $page, pageSize: $pageSize) {
              results { 
                caseTypeId
                cases { 
                  id
                  version
                  caseTypeId
                  caseType
                  title
                  currentState
                  properties
                  createdAt
                  updatedAt
                  lastExportedAt
                  lastExportedViaBucketId
                  noUnreadConversations
                  unresolvedTaskFailures
                  serviceLevels {
                    active
                    runningTime
                    jeopardizedAt
                    breachedAt
                    serviceLevelCriteria { id name }
                  }
                }
                total
                error
                message
                lowLevelErrorMessage  
              }
              documents {
                total
                error
                message
                documents {
                  id
                  filename
                  extension
                  contentType
                  size
                  url
                  caseId
                  caseTypeId
                  caseTitle
                  createdAt
                  highlight
                }
              }
              matchTotal
              caseMatchTotal
              documentMatchTotal
              matches { kind score id caseTypeId caseId title currentState updatedAt createdAt matchedField highlight filename extension contentType size url caseTitle latestActivity }
              caseMatches { kind score id caseTypeId caseId title currentState updatedAt createdAt matchedField highlight filename extension contentType size url caseTitle latestActivity }
              documentMatches { kind score id caseTypeId caseId title currentState updatedAt createdAt matchedField highlight filename extension contentType size url caseTitle latestActivity }
            }
          }
        `),
      variables: { searchTerm: searchTerm, page: page || 0, pageSize: pageSize || 20 },
    })
    if (!response || !response.data || !response.data.globalSearch) {
      return invalidSearchResponse()
    }
    return response.data.globalSearch
  } catch (error) {
    console.error(error)
    return invalidSearchResponse(error)
  }
}

export const getCaseTypeSearch = async (searchTerm, caseTypeId, page, pageSize, sortField, sortDirection) => {
  try {
    const response = await apollo.query({
      query: gql(`
          query caseTypeSearch($searchTerm: String!, $caseTypeId: Int!, $page: Int!, $pageSize: Int, $sortField: String, $sortDirection: String) {
            caseTypeSearch(searchTerm: $searchTerm, caseTypeId: $caseTypeId, page: $page, pageSize: $pageSize, sortField: $sortField, sortDirection: $sortDirection) {
              caseTypeId
              cases { 
                id
                version
                caseTypeId
                caseType
                title
                currentState
                properties
                createdAt
                updatedAt
                lastExportedAt
                lastExportedViaBucketId
                noUnreadConversations
                unresolvedTaskFailures
                serviceLevels {
                  active
                  runningTime
                  jeopardizedAt
                  breachedAt
                  serviceLevelCriteria { id name }
                }
               }
              total
              error
              message
              lowLevelErrorMessage
              documents {
                total
                error
                message
                documents {
                  id
                  filename
                  extension
                  contentType
                  size
                  url
                  caseId
                  caseTypeId
                  caseTitle
                  createdAt
                  highlight
                }
              }
            }
          }
        `),
      variables: { searchTerm: searchTerm, caseTypeId: caseTypeId, page: page, pageSize: pageSize, sortField: sortField, sortDirection: sortDirection },
    })
    if (!response || !response.data || !response.data.caseTypeSearch) {
      return invalidSearchResponse()
    }
    return response.data.caseTypeSearch
  } catch (error) {
    console.error(error)
    return invalidSearchResponse(error)
  }
}

export const subscribeToCaseEvents = async (caseId, eventType) => {
  try {
    const response = await apollo.mutate({
      mutation: gql(
        `
          mutation subscribeToCaseEvent($caseId: Int!, $eventType: String!) {
            subscribeToCaseEvent(caseId: $caseId, eventType: $eventType) {
              eventSubscription { id case { id } }
              error
              lowLevelErrorMessage
              message
            }
          }
        `
      ),
      variables: { caseId: caseId, eventType: eventType },
    })
    return response.data.subscribeToCaseEvent
  } catch (error) {
    console.error(error)
  }
}
export const unsubscribeFromCaseEvents = async (caseId, eventType) => {
  try {
    const response = await apollo.mutate({
      mutation: gql(
        `
          mutation unsubscribeFromCaseEvent($caseId: Int!, $eventType: String!) {
            unsubscribeFromCaseEvent(caseId: $caseId, eventType: $eventType) {
              error
              lowLevelErrorMessage
              message
            }
          }
        `
      ),
      variables: { caseId: caseId, eventType: eventType },
    })
    return response.data.unsubscribeFromCaseEvent
  } catch (error) {
    console.error(error)
  }
}

export const createCaseNote = async (caseId, { subject, body }) => {
  caseId = parseInt(caseId, 10)
  const response = await apollo.mutate({
    mutation: gql(
      `
        mutation createCaseNote($caseId: Int!, $subject: String!, $body: String!) {
          createCaseNote(caseId: $caseId, subject: $subject, body: $body) {
            error
            lowLevelErrorMessage
            message
            caseNote {
              id
              subject
              body
              createdAt
              updatedAt
              author {
                id
                displayName
              }
            }
          }
        }
      `
    ),
    variables: { caseId, subject, body },
  })
  return response.data.createCaseNote
}

export const updateCaseNote = async ({ id, subject, body }) => {
  id = parseInt(id, 10)
  const response = await apollo.mutate({
    mutation: gql(
      `
        mutation updateCaseNote($id: Int!, $subject: String!, $body: String!) {
          updateCaseNote(id: $id, subject: $subject, body: $body) {
            error
            lowLevelErrorMessage
            message
            caseNote {
              id
              subject
              body
              createdAt
              updatedAt
              author {
                id
                displayName
              }
            }
          }
        }
      `
    ),
    variables: { id, subject, body },
  })
  return response.data.updateCaseNote
}

export const deleteCaseNote = async (id) => {
  id = parseInt(id, 10)
  const response = await apollo.mutate({
    mutation: gql(`
        mutation deleteCaseNote($id: Int!) {
          deleteCaseNote(id: $id) {
            error
            lowLevelErrorMessage
            message
          }
        }
      `),
    variables: { id },
  })
  return response.data.deleteCaseNote
}

export const resolveTaskFailures = async (caseTaskId) => {
  try {
    const response = await apollo.mutate({
      mutation: gql(`
          mutation resolveCaseTaskFailures($caseTaskId: String!) {
            resolveCaseTaskFailures(caseTaskId: $caseTaskId) {
              error
              message
              lowLevelErrorMessage
            }
          }        `),
      variables: { caseTaskId },
    })
    return response.data.resolveCaseTaskFailures
  } catch (error) {
    console.error(error)
  }
}

const parseRequiredInt = (value) => {
  if (value == null || value === "") return null
  const parsed = parseInt(value, 10)
  if (isNaN(parsed)) return null
  return parsed
}

export const enterCase = async (caseId) => {
  const id = parseRequiredInt(caseId)
  if (id == null) return
  try {
    clearTimeout(currentTimeout)
    await apollo.mutate({
      mutation: gql(`
        mutation enterCase($caseId: Int!) {
          enterCase(caseId: $caseId) {
            viewers { id displayName }
            message
            error
            lowLevelErrorMessage
          }
        }`),
      variables: { caseId: id },
    })
  } catch (error) {
    console.error(error)
  }
  refreshSubscription(id)
}

export const exitCase = async (caseId) => {
  const id = parseRequiredInt(caseId)
  if (id == null) return
  try {
    await apollo.mutate({
      mutation: gql(`
        mutation exitCase($caseId: Int!) {
          exitCase(caseId: $caseId) {
            viewers { id displayName }
            message
            error
            lowLevelErrorMessage
          }
        }`),
      variables: { caseId: id },
    })
    clearTimeout(currentTimeout)
  } catch (error) {
    console.error(error)
  }
}

export const subscribeToCasePresence = async (caseId) => {
  const id = parseRequiredInt(caseId)
  if (id == null) return
  const caseViewerObserver = await apollo
    .subscribe({
      query: gql(`
      subscription currentViewers($caseId: Int!) {
        currentViewers(caseId: $caseId) {
          viewers { id displayName }
          error
          message
          lowLevelErrorMessage
        }
      }
    `),
      variables: { caseId: id },
    })
    .subscribe({
      next(response) {
        Vue.store.dispatch("setCurrentCaseViewers", response.data.currentViewers.viewers)
      },
      error(err) {
        console.error("Subscription error: ", err.message)
      },
    })
  return caseViewerObserver
}

export const unsubscribeFromCasePresence = async (caseViewerObserver) => {
  if (caseViewerObserver) {
    return caseViewerObserver.unsubscribe()
  }
}

let currentTimeout = null
const refreshSubscription = async (caseId) => {
  currentTimeout = setTimeout(async () => {
    const token = window.localStorage.getItem("session")
    const validJwt = Helpers.isJwtValid(token)
    const usingScanGUI = window.location.href.match(/scangui/)
    if (validJwt || usingScanGUI) {
      enterCase(caseId)
    } else {
      clearTimeout(currentTimeout)
      Vue.router.push("/login")
    }
  }, 10000)
}

const bucketObservers = []
export const subscribeToBucketPresence = async (caseIds) => {
  for (const caseId of caseIds) {
    const bucketCaseObserver = apollo
      .subscribe({
        query: gql(`
        subscription currentViewers($caseId: Int!) {
          currentViewers(caseId: $caseId) {
            viewers { id displayName }
            error
            message
            lowLevelErrorMessage
          }
        }
      `),
        variables: { caseId: parseInt(caseId, 10) },
      })
      .subscribe({
        next(response) {
          Vue.store.commit("setBucketCaseViewers", { caseId, viewers: response.data.currentViewers })
        },
        error(err) {
          console.log("Subscription error: ", err.message)
        },
      })
    bucketObservers.push(bucketCaseObserver)
  }
}

export const unsubscribeFromBucketPresence = async () => {
  for (const bucketCaseObserver of bucketObservers) {
    if (bucketCaseObserver) {
      bucketCaseObserver.unsubscribe()
    }
  }
}
